<template>
  <div v-if="!hidden" class="inline-rating" data-testid="inline-rating">
    <template v-if="!hasRated">
      <div class="prompt">กรุณาประเมินการบริการ</div>
      <RatingChoices size="inline" :selected="selected" :disabled="submitting" @select="onSelect" />
      <p v-if="errorMessage" class="retry-message" data-testid="inline-rating-retry-message">{{ errorMessage }}</p>
    </template>
    <div v-else class="confirmed" data-testid="inline-rating-confirmed">
      <span class="confirmed-text">ได้รับการประเมินแล้ว</span>
    </div>
  </div>
</template>

<script>
import RatingChoices from './RatingChoices.vue';
import { watchUntil } from './scheduling';
import { API_BASE_URL } from './apiBaseUrl';
import { submitRating, OUTCOME } from '@shared/customer-display/submitRating';
import { isInlineRatingAvailable } from '@shared/customer-display/protocol';

export default {
  name: 'InlineRatingSection',
  components: { RatingChoices },
  props: {
    ratingSession: { type: Object, required: true }
  },
  // Acknowledged up to CustomerDisplayPage so the owning publisher can learn
  // the session is rated, or no longer ratable (direct-submit stays exactly
  // as-is; these never send the rating itself back through the cashier, only
  // the small completion/unavailable fact) - otherwise a display refresh
  // would replay stale tap controls.
  data() {
    return {
      errorMessage: '',
      hidden: !isInlineRatingAvailable(this.ratingSession) && this.ratingSession.status !== 'rated',
      confirmed: this.ratingSession.status === 'rated',
      localRating: this.ratingSession.status === 'rated' ? this.ratingSession.rating : null,
      // Which icon was just tapped, for RatingChoices' own grow/fade
      // feedback (mirrors RatingScreen.vue's identical `selected` field) --
      // purely visual, set optimistically in onSelect before the submit
      // resolves, cleared on a network-error retry or a brand new session.
      selected: null,
      // This component instance is reused across customers/documents
      // (ActiveScreen never gives it a :key), so a request started for one
      // ratingSession.id can still be in flight when the next one takes
      // over. Track it by id rather than a plain boolean, so a brand new
      // session is never blocked by - or accidentally resolved by - a stale
      // request that belongs to a previous one (Phase 4 review finding #3).
      pendingSessionId: null
    };
  },
  computed: {
    submitting() {
      return this.pendingSessionId === this.ratingSession.id;
    },
    hasRated() {
      return this.ratingSession.status === 'rated' || this.confirmed;
    }
  },
  watch: {
    // A fresh session (a new document) must never show the previous
    // customer's rated confirmation, stale error, or expiry countdown.
    'ratingSession.id'() {
      this.confirmed = this.ratingSession.status === 'rated';
      this.localRating = this.confirmed ? this.ratingSession.rating : null;
      this.hidden = !isInlineRatingAvailable(this.ratingSession) && this.ratingSession.status !== 'rated';
      this.errorMessage = '';
      this.selected = null;
      this.customerDisplayWatchExpiry();
    }
  },
  created() {
    this.customerDisplayCancelExpiryWatch = null;
    this.customerDisplayWatchExpiry();
    // Set once in beforeDestroy, checked after the await in onSelect below
    // alongside the existing sessionId check -- ActiveScreen.vue's
    // showInlineRating can go false (unmounting this component) while a
    // submit is still in flight, so the sessionId guard alone is not quite
    // enough: a brand new ratingSession with the SAME id could in principle
    // reappear later, but there is no reason to apply a stale result to a
    // now-destroyed instance either way.
    this.customerDisplayDestroyed = false;
  },
  beforeDestroy() {
    this.customerDisplayDestroyed = true;
    if (this.customerDisplayCancelExpiryWatch) this.customerDisplayCancelExpiryWatch();
  },
  methods: {
    customerDisplayWatchExpiry() {
      if (this.customerDisplayCancelExpiryWatch) this.customerDisplayCancelExpiryWatch();
      const sessionId = this.ratingSession.id;
      this.customerDisplayCancelExpiryWatch = watchUntil(this.ratingSession.expiresAt, () => {
        this.hidden = true;
        this.$emit('unavailable', { sessionId });
      });
    },
    // Returns the submit result (so a Phase 6 hardware-remote intent can
    // report it back over the helper bridge via submitFromHelper below), or
    // null when this tap was a no-op (already submitting, already rated, or
    // a stale session raced in) -- the caller then reports 'unavailable'
    // rather than fabricating an outcome. Mirrors RatingScreen.vue's
    // onSelect/submitFromHelper pair exactly, so a tap and a helper-
    // triggered press share the identical guard/submit/outcome-handling
    // code -- nothing rating/session/backend-related is duplicated for the
    // hardware path.
    async onSelect(rating) {
      if (this.submitting || this.hasRated) return null; // one effective rating only
      const sessionId = this.ratingSession.id;
      const ratingToken = this.ratingSession.ratingToken;
      this.pendingSessionId = sessionId;
      this.selected = rating;
      this.errorMessage = '';
      const result = await submitRating({ baseUrl: API_BASE_URL, sessionId, ratingToken, rating });
      if (this.pendingSessionId === sessionId) this.pendingSessionId = null;
      // A different session now owns this widget (a new document took over
      // while this request was in flight), or this instance was torn down
      // entirely - this result belongs to nobody currently on screen and
      // must never touch rated/error/hidden state.
      if (this.customerDisplayDestroyed || this.ratingSession.id !== sessionId) return null;

      if (result.outcome === OUTCOME.SUCCESS || result.outcome === OUTCOME.ALREADY_RATED) {
        // Backend truth wins, for the ON-SCREEN confirmation -- never the
        // rating this tap itself just submitted. For SUCCESS,
        // submitRating.js's own isValidRatedResult check already requires
        // result.session.rating to equal the submitted rating before it
        // will even report SUCCESS, so in practice these are the same
        // value; preferring result.session.rating here regardless removes
        // any dependency on that invariant holding for what the CUSTOMER
        // sees. Without a reconcilable rating (the neutral-confirmation
        // ALREADY_RATED case), fall back to null rather than assuming the
        // tap that may have lost a race is the value that stuck (Phase 4
        // review finding #5). `submittedRating` (the raw local value, not
        // reconciled) travels alongside it so the parent can separately
        // decide whether an RP2040 ACK would be truthful for THIS press --
        // see CustomerDisplayPage.vue's sendAckForOwnSubmission and Codex
        // review: "ALREADY_RATED can send ACK:n for a different rating than
        // the physical button actually submitted".
        this.confirmed = true;
        const reconciled = result.session && result.session.rating ? result.session.rating : null;
        this.localRating = reconciled;
        this.$emit('rated', { sessionId, rating: reconciled, submittedRating: rating });
        return result;
      }
      if (result.outcome === OUTCOME.EXPIRED || result.outcome === OUTCOME.INVALID) {
        // Terminal/invalid: remove the widget rather than offer a dead
        // control, and let the owning publisher know so a replay does not
        // resend it either (Phase 4 review finding #2).
        this.hidden = true;
        this.$emit('unavailable', { sessionId });
        return result;
      }
      // NETWORK_ERROR: re-enable controls, short customer-safe retry message.
      // The emit is purely observational (RP2040 RETRY cue, see
      // CustomerDisplayPage.vue's onNetworkError) -- it changes nothing
      // about this guard/retry/outcome handling above.
      this.selected = null;
      this.errorMessage = 'ไม่สามารถบันทึกคะแนนได้ กรุณาลองอีกครั้ง';
      this.$emit('network-error', { sessionId });
      return result;
    },
    // Vue 2 Options API exposes every method on the instance already -- this
    // is reachable by ActiveScreen.vue via $refs, the same pattern
    // RatingScreen.vue/CustomerDisplayPage.vue use for the full-screen case.
    submitFromHelper(rating) {
      return this.onSelect(rating);
    }
  }
};
</script>

<style scoped>
.inline-rating {
  width: 100%;
  margin-top: 1.5vh;
  padding: 1.5vh 1rem;
  background: #fef2f2;
  border: 2px solid #fecaca;
  border-radius: 0.75rem;
  text-align: center;
}
.prompt {
  font-size: calc(1.3rem * var(--font-scale));
  font-weight: 700;
  color: #4c1d95;
  margin-bottom: 1vh;
}
.retry-message {
  margin: 0.6vh 0 0;
  font-size: calc(0.85rem * var(--font-scale));
  color: #b91c1c;
}
.confirmed {
  display: flex;
  align-items: center;
  justify-content: center;
}
.confirmed-text {
  font-size: calc(1.05rem * var(--font-scale));
  font-weight: 600;
  color: #4c1d95;
}
</style>
